import { getMobileWidgetDefinition } from '../../analyticsWidgets';
import AnalyticsViewWidget from './analyticsView';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  getWidgetConfig,
  replaceCustomVariables,
  getWidgetOptions,
  getWidgetQuery,
  getStringfiedField
} from '../../utils/widgets';
import { getSelectedProject } from '../../redux/projects/actions';
import { getSelectedAnalyticsDashboard, getSelectedSummaryDashboard } from '../../redux/templates/actions';
import { getSpeedUnitOption, getMultiplyValues } from '../../utils/project';
import Heatmap from './heatmap';
import { getIntegrations } from '../../redux/integration/actions';
import { getCmsInfo } from '../../utils/integrations';
import FunnelChart from './funnel';
import PosReport from './posReport';
import EstimatedImpressionsWidget from './estimatedImpressions';
import CmsImpressionsWidget from './cmsImpressions';
import { getEstimatedImpressionsViewId, isRetailMediaTemplate } from '../../utils/analytics/estimatedImpressions';

const Widget = ({
  widget,
  filters,
  lastUpdated,
  handleError,
  eventHandlers,
  collection = 'SUMMARY',
  customDashboard,
  isBrand = false,
  portal
}) => {
  const project = filters.brandCompany || useSelector(getSelectedProject);
  const integrations = useSelector(getIntegrations);
  const selectedDashboard = customDashboard
    ? customDashboard
    : collection === 'SUMMARY'
      ? useSelector(getSelectedSummaryDashboard)
      : useSelector(getSelectedAnalyticsDashboard);

  const isMobileDashboard = selectedDashboard?.headers === 'mobile';

  const hasDevices = filters?.selectedDevices && filters.selectedDevices.length > 0;

  if (!project || !selectedDashboard) {
    return null;
  }

  if (widget?.cmsImpressions) {
    return <CmsImpressionsWidget widget={widget} filters={filters} project={project} lastUpdated={lastUpdated} />;
  }

  const mobileDefinition = getMobileWidgetDefinition(widget, selectedDashboard);
  if (mobileDefinition) {
    if (mobileDefinition.resultKind === 'funnel') {
      return <FunnelChart widget={mobileDefinition} query={filters} projectOverride={project}
        portal={portal} isBrand={isBrand} lastUpdated={lastUpdated} />;
    }
    return <AnalyticsViewWidget definition={mobileDefinition} filters={filters} project={project}
      portal={portal} isBrand={isBrand} lastUpdated={lastUpdated} />;
  }

  // Determine whether this widget generally requires devices to render.
  // Use a few heuristics: explicit wifi/heatmap flags, requiredColumns referencing deviceId,
  // or presence of a `devices` entry in the widget query. This keeps the original
  // validation behavior for widgets that actually need devices while allowing
  // widgets that don't depend on devices to render when no devices are selected.
  const widgetNeedsDevices = (() => {
    if (!widget) return false;
    if (widget.wifiTracker) return true;
    if (widget.display === 'heatmap') return true;
    if (widget.display === 'funnel') return true;
    if (Array.isArray(widget.requiredColumns) && widget.requiredColumns.includes('deviceId')) return true;
    if (widget.query && Object.prototype.hasOwnProperty.call(widget.query, 'devices')) return true;
    return false;
  })();

  if (!isMobileDashboard && !hasDevices && collection !== 'DISCOVERY' && widgetNeedsDevices) {
    return null;
  }

  const integration = getCmsInfo(selectedDashboard.cmsId, integrations);
  const speedUnitOptions = getSpeedUnitOption(project.preferences.speedUnit);
  const multiplyValues = getMultiplyValues(project.preferences.vehicleMultiplier);
  const currentCMS = integration
    ? {
        name: integration.vendor.toLowerCase(),
        table: integration.sourceTable,
        columns: integration.popColumnMappings,
        playlogTable: integration.vendor.toLowerCase() + 'v1' // TODO: FIX THIS.
      }
    : null;

  // Inject custom variables
  const variableToReplace = project
    ? {
        ...project.preferences,
        projectId: project.id,
        solutionId: selectedDashboard.table,
        conversionFactor: speedUnitOptions.conversionFactor,
        speedUnit: speedUnitOptions.shortName,
        interval: filters?.interval?.name ? filters.interval.name : '',
        multiplyValues,
        siteColumn: integration ? integration.popColumnMappings.sites : '',
        mediaColumn: integration ? integration.popColumnMappings.medias : '',
        advertiserColumn: integration ? integration.popColumnMappings.advertisers : '',
        cmsDescriptor: integration ? JSON.stringify(currentCMS) : '',
        // Add filters to cover Mobile Data params
        ...filters
      }
    : {};

  const newWidget = replaceCustomVariables(widget, variableToReplace);

  if (!newWidget) {
    return null;
  }

  if (newWidget?.requestResult) {
    // Listen to requestResult
    eventHandlers.push(newWidget.requestResult);
    window.addEventListener('requestResult', newWidget.requestResult);
  }

  // Listen to requestError
  eventHandlers.push(handleError);
  window.addEventListener('requestError', handleError);

  if (widget.display === 'heatmap') {
    return <Heatmap query={filters} widget={newWidget} />;
  }

  if (widget.display === 'funnel') {
    if (isBrand) {
      return null;
    }
    return <FunnelChart widget={newWidget} query={filters} />;
  }

  if (widget.display === 'pos') {
    if (!isBrand || !portal?.activeFeatures?.includes('POS')) {
      return null;
    }
    const posWidget = replaceCustomVariables(widget, { bpid: portal.id });
    return <PosReport widget={posWidget} query={filters} lastUpdated={lastUpdated} portal={portal} />;
  }

  if ((isRetailMediaTemplate(selectedDashboard) || widget.id === 'estimated-impression') && getEstimatedImpressionsViewId(widget, selectedDashboard)) {
    return (
      <EstimatedImpressionsWidget
        widget={newWidget}
        filters={filters}
        project={project}
        lastUpdated={lastUpdated}
        handleError={handleError}
      />
    );
  }

  return (
    <widget-container
      key={`analytics-${newWidget.id}`}
      id={newWidget.id}
      info={getStringfiedField(newWidget, 'info')}
      format={newWidget.format}
      unit={newWidget.unit}
      plot-options={getStringfiedField(newWidget, 'plotOptions')}
      widget-title={newWidget.title}
      data={newWidget.data}
      display={newWidget.display}
      config={getWidgetConfig(newWidget.config, project.companyId)}
      options={getWidgetOptions(newWidget)}
      query={getWidgetQuery(newWidget, filters, selectedDashboard, integration?.popColumnMappings, lastUpdated)}
      is-loading={filters?.loading}
      y-title={newWidget.yTitle}
      y-title-secondary={widget.yTitleSecondary}
    />
  );
};

export default Widget;
